import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { InvalidTokenError } from 'jwt-decode';

import { isDefined, isNotDefined, retrieveToken } from '@hsp/utilities';

import { customEvents } from 'constants/custom-events';
import { Uri } from 'constants/uri';
import { MAX_ITEMS, NO_RETRY_HEADER } from 'services/http/constants/headers';
import { CancelError } from 'services/http/errors';
import { HttpHeaders } from 'services/http/http-headers';
import { refreshToken } from 'services/utilities';
import { StatusCode } from 'types/enums/status-code';
import { TokenMissingError } from 'types/errors';
import { RemoveInterceptor } from 'types/services/http/remove-interceptor';
import { publishCustomEvent } from 'utilities/events';

const onRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const token = retrieveToken();

  if (!isDefined(token)) {
    return config;
  }

  config.headers = new HttpHeaders(config.headers).addAuthentication(token.token).addHeader({
    name: MAX_ITEMS,
    value: process.env.REACT_APP_MAX_ITEMS ?? '100',
  }).headers;

  return config;
};

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  // eslint-disable-next-line no-console
  console.error(`[jwt token error] [${JSON.stringify(error)}]`);

  return Promise.reject(error);
};

const onResponseError = async (error: AxiosError, instance: AxiosInstance): Promise<AxiosError> => {
  const { config: originalRequest, response: errorResponse } = error;
  const token = retrieveToken();

  if (axios.isCancel(error)) {
    return Promise.reject(new CancelError());
  }

  if (!isDefined(token)) {
    return Promise.reject(error);
  }

  if (!errorResponse?.config.url?.includes(Uri.Authentication.Token.login)) {
    switch (errorResponse?.status) {
      case StatusCode.NetworkAuthenticationRequired:
        publishCustomEvent(customEvents.logOffEvent);

        return Promise.reject(new InvalidTokenError());

      case StatusCode.Unauthorized: {
        if (!isDefined(originalRequest)) {
          return Promise.reject(error);
        }

        const httpHeaders = new HttpHeaders(originalRequest.headers);
        const headers = httpHeaders.headers;

        if (isDefined(headers) && headers.get(NO_RETRY_HEADER) === 'true') {
          return Promise.reject(error);
        }

        if (!isDefined(token)) {
          return Promise.reject(new TokenMissingError());
        }

        const response = await refreshToken(instance, token);

        if (isNotDefined(response) || isNotDefined(response.token)) {
          return Promise.reject(new TokenMissingError());
        }

        publishCustomEvent(customEvents.newTokenEvent, response);

        httpHeaders.addAuthentication(response.token).addHeader({ name: NO_RETRY_HEADER, value: 'true' });

        originalRequest.headers = httpHeaders.headers;

        return instance(originalRequest);
      }

      case StatusCode.UnprocessableEntity:
        return Promise.reject(error);
    }

    publishCustomEvent(customEvents.apiErrorEvent, errorResponse?.data);

    return Promise.reject(error);
  }

  return Promise.reject(error);
};

export const addJwtTokenInterceptor = (instance: AxiosInstance): RemoveInterceptor => {
  const requestId = instance.interceptors.request.use(onRequest, onRequestError);
  const responseId = instance.interceptors.response.use(
    (response) => response,
    (error) => onResponseError(error, instance)
  );

  return () => {
    instance.interceptors.request.eject(requestId);
    instance.interceptors.response.eject(responseId);
  };
};
